import {useCallback, useEffect, useRef, useState} from 'react';
import {createCx} from '@utils/cx';
import {useSinglePlayerControl} from '@hooks/useSinglePlayerControl';
import styles from './MediaPreviewModal.module.less';

const cx = createCx(styles);

export interface MediaPreviewConfig {
    /** 预览媒体地址 */
    src: string;
    /** 媒体类型 */
    type: 'image' | 'video';
    /** 点击媒体之外的区域是否关闭弹窗，默认 false */
    closeOnClickBlank?: boolean;
    /** closeOnClickBlank 为 true 时，点击空白区域触发的关闭回调 */
    onClose?: () => void;
}

/**
 * 媒体预览内容组件
 *
 * 供 `showMediaPreview()` API 内部使用，也可作为普通组件单独使用。
 */
const MediaPreviewContent: React.FC<MediaPreviewConfig> = ({
    src,
    type,
    closeOnClickBlank,
    onClose,
}) => {
    const isVideo = type === 'video';
    const wrapRef = useRef<HTMLDivElement>(null);
    const [orientation, setOrientation] = useState<'landscape' | 'portrait' | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const {activatePlayer} = useSinglePlayerControl();

    // src 变更时重置 orientation，避免用上一个媒体的布局状态
    useEffect(() => {
        setOrientation(null);
    }, [src]);

    // 根据媒体与容器宽高比决定锁宽还是锁高，避免 letterbox 遮住空白点击区
    const detectOrientation = useCallback((mediaW: number, mediaH: number) => {
        const wrap = wrapRef.current;
        if (!wrap || !mediaW || !mediaH) {
            return;
        }
        setOrientation(
            mediaW / mediaH > wrap.clientWidth / wrap.clientHeight ? 'landscape' : 'portrait',
        );
    }, []);

    useEffect(() => {
        const wrap = wrapRef.current;
        const container = wrap?.closest('.ant-modal-container');
        if (!closeOnClickBlank || !wrap || !container) {
            return () => {};
        }
        const handler = (e: Event) => {
            if (e.target === container || e.target === wrap) {
                onCloseRef.current?.();
            }
        };
        container.addEventListener('click', handler);
        return () => {
            container.removeEventListener('click', handler);
        };
    }, [closeOnClickBlank]);

    return (
        <div ref={wrapRef} className={cx('mediaWrap')}>
            {isVideo ? (
                <video
                    src={src}
                    controls
                    autoPlay
                    className={cx('media', orientation)}
                    onLoadedMetadata={(e) =>
                        detectOrientation(e.currentTarget.videoWidth, e.currentTarget.videoHeight)
                    }
                    onPlay={(e) => activatePlayer(e.currentTarget)}
                />
            ) : (
                <img
                    src={src}
                    alt=""
                    className={cx('media', orientation)}
                    onLoad={(e) =>
                        detectOrientation(
                            e.currentTarget.naturalWidth,
                            e.currentTarget.naturalHeight,
                        )
                    }
                />
            )}
        </div>
    );
};

export default MediaPreviewContent;
